import { MerchTile, MoreDetailsOptions } from '@carvana/merch-tile';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { assetsCdnUrl, vexCdnUrl } from '~/constants/environment';
import { InventoryTypeId } from '~/constants/inventoryType';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useConfiguration, useConsumer } from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import {
  useFavoritesIds,
  useUserLocation,
  useIsLoggedIn
} from '~/context/common/UserContext';
import { useHasSoftPull } from '~/context/srp/FinanceContext';
import {
  useInventoryBulkData,
  useSearchRequestId
} from '~/context/srp/InventoryContext';
import ModalContext from '~/context/srp/ModalContext';
import { useShouldUpdateTerms } from '~/context/srp/PricingContext';
import { useTrackEvent } from '~/context/srp/SearchAnalyticsContext';
import { useShowFinance } from '~/context/srp/SearchContext';
import { TileData } from '~/context/srp/TileDataContext';
import { useToggleFavorite } from '~/hooks/common/favorites';
import useExperimentBucket from '~/hooks/common/useExperimentBucket';
import { useHasActiveTradeApplied } from '~/hooks/srp/selectors';
import useSendTileInfoEvent from './useSendTileInfoEvent';
import useSetSearchRequestIdCookie from './useSetSearchRequestIdCookie';

type OwnProps = {
  vehicle: TileData;
  calculateEvWidth: boolean;
};

export default function VehicleTile({ calculateEvWidth, vehicle }: OwnProps) {
  const isMobile = useIsMobile();
  const hasSoftPull = useHasSoftPull();
  const favoriteIds = useFavoritesIds();
  const { showCkPromo = false } = useConfiguration();
  const hasTradeInApplied = useHasActiveTradeApplied();
  const [ref, inView] = useInView({
    root: null,
    rootMargin: isMobile ? '-49% 0px -49% 0px' : '90px 0px 0px', // Vehicle tile is intersecting the middle 2% of the viewport
    threshold: isMobile ? 0 : 0.5
  });
  const searchRequestId = useSearchRequestId();
  const userLocation = useUserLocation();
  const { setWipNotificationModalOpen, setMoreDetailsModalOpen } =
    useContext(ModalContext);
  const trackEvent = useTrackEvent();
  const termsAreUpdating = useShouldUpdateTerms();
  const { isTradeInApplied } = useContext(TradeContext);
  const toggleFavorite = useToggleFavorite({
    onAddFavorite: () => {
      if (vehicle.vehicleInventoryType === InventoryTypeId.WIP) {
        setWipNotificationModalOpen(true, { vehicleId: vehicle.vehicleId });
      }
    }
  });
  const isLoggedIn = useIsLoggedIn();
  const [isFavoritedLocally, setFavoritedLocally] = useState(
    favoriteIds.includes(vehicle.vehicleId)
  );
  const showFinanceInfo = useShowFinance();
  const inventoryBulkData = useInventoryBulkData();

  const setSearchRequestCookieId = useSetSearchRequestIdCookie();
  const sendTileInfoEvent = useSendTileInfoEvent();

  const consumer = useConsumer();

  function handleOnClick() {
    setSearchRequestCookieId();
    sendTileInfoEvent(vehicle);
  }

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const { vehicleId, stockNumber } = vehicle;
      const customAttributes = {
        action: isFavoritedLocally ? 'remove' : 'add',
        description: 'search tile heart cta',
        vehicleId
      };

      toggleFavorite({ vehicleId, stockNumber, nativeEventSource: 'srp' });
      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Clicked - Merch Tile Favorite', customAttributes);
    },
    [isFavoritedLocally, isLoggedIn, toggleFavorite, trackEvent, vehicle]
  );

  useEffect(() => {
    setFavoritedLocally(favoriteIds.includes(vehicle.vehicleId));
  }, [favoriteIds, vehicle.vehicleId]);

  const openModal = (options: MoreDetailsOptions) => {
    setMoreDetailsModalOpen(true, options);
  };

  return (
    <MerchTile
      assets={{
        cvnaVexFastlyImageUrl: vexCdnUrl,
        cvnaAppAssetsCdn: assetsCdnUrl
      }}
      availabilityInfo={{
        pickup:
          inventoryBulkData[vehicle.vehicleId]?.soonestPickup.content ??
          undefined,
        delivery:
          inventoryBulkData[vehicle.vehicleId]?.soonestDelivery.content ??
          undefined
      }}
      configurations={{
        showCkPromo
      }}
      consumer={consumer}
      coreData={vehicle}
      experiments={{
        'car-merch-srp-tile-shipping-fee-and-cash-down': useExperimentBucket(
          'car-merch-srp-tile-shipping-fee-and-cash-down'
        ),
        'merch-favorites-and-saved-searches': useExperimentBucket(
          'merch-favorites-and-saved-searches'
        )
      }}
      financeInfo={{ ...vehicle.price, termInMonths: vehicle.price.term }}
      hasBorder={!isMobile}
      isFavorite={isFavoritedLocally}
      inVerticalStack={isMobile}
      inView={inView}
      ref={ref}
      showFinanceInfo={showFinanceInfo}
      showSplatting={true}
      userInfo={{
        locationState: userLocation.state,
        hasSoftPull,
        tradeInApplied: hasTradeInApplied
      }}
      utils={{
        handleFavoriteClick: handleFavorite,
        handleTileClick: handleOnClick,
        openMoreDetailsModal: openModal,
        trackEvent,
        termsUpdateState: {
          termsAreUpdating: termsAreUpdating,
          tradeUpdateState: isTradeInApplied
        },
        shouldCalculateEvLength: calculateEvWidth,
        searchRequestId: searchRequestId
      }}
    />
  );
}
